/**
 * /p/orcamento/:token — Pagina publica do orcamento (Bloco G).
 *
 * SEM autenticacao. Cliente acessa pelo link compartilhado, ve o orcamento
 * e clica Aprovar ou Recusar. Tela minimalista, mobile-first, sem chrome
 * do ERP (nao tem topbar/sidebar — eh rota fora do AuthenticatedShell).
 *
 * Estados:
 *   - loading: carregando
 *   - erro: 404 (token invalido), 410 (expirado/revogado), 429 (rate limit), outro
 *   - dados: orcamento + botoes (se pode_aprovar)
 *   - confirmando: aprovar ou recusar
 *   - sucesso: tela de "obrigado"
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FiAlertCircle,
  FiCheck,
  FiClock,
  FiDownload,
  FiEdit3,
  FiX,
} from "react-icons/fi";

import HtmlTemplateFrame from "../../components/HtmlTemplateFrame";
import { ACCESS_API_URL } from "../../config/api";
import {
  aprovarOrcamentoPublico,
  recusarOrcamentoPublico,
  visualizarOrcamentoPublico,
} from "../../services/orcamentoLinkPublico";
import {
  Container,
  Header,
  HeaderInner,
  Card,
  Section,
  AmbienteCard,
  AmbienteHeader,
  ItensTable,
  TotalRow,
  ActionArea,
  ActionBar,
  ApproveButton,
  RejectButton,
  StateBox,
  Title,
  Subtitle,
  Label,
  Value,
  Textarea,
  Modal,
  ModalContent,
  ModalActions,
  PrimaryButton,
  SecondaryButton,
} from "./styles";

const fmtMoeda = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
};

const SITUACAO_LABEL = {
  RASCUNHO: "Rascunho",
  EM_NEGOCIACAO: "Em negociação",
  APROVADO: "Aprovado",
  CONVERTIDO: "Convertido em pedido",
  RECUSADO: "Recusado",
  EXPIRADO: "Expirado",
};

function OrcamentoPublico() {
  const { token } = useParams();
  const [estado, setEstado] = useState({ loading: true, erro: null, dados: null });
  const [confirmando, setConfirmando] = useState(null); // "aprovar" | "recusar"
  // Falha de AÇÃO é diferente de falha ao CARREGAR. Reteste 2026-08-18:
  // o erro de aprovar caía em `estado.erro`, e o guard de render trocava
  // a página inteira pela caixa "Não foi possível carregar" — título
  // falso (a proposta tinha carregado) e a proposta sumia da tela do
  // cliente, que ficava sem saber o que tinha acabado de ver.
  const [erroAcao, setErroAcao] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(null);

  // Proposta no template padrao da unidade (quando publicado+padrao).
  // Renderizada num iframe isolado (HtmlTemplateFrame) — preserva o bloco
  // <style> do template sem vazar o CSS para esta pagina.
  const htmlTemplate = estado.dados?.html_template || "";

  // Copia em PDF (mesmo template; backend tem fallback de layout minimo —
  // o botao funciona com ou sem template configurado).
  const urlPdf = `${ACCESS_API_URL}/publico/orcamento/${token}/pdf`;

  const carregar = async () => {
    setEstado({ loading: true, erro: null, dados: null });
    try {
      const dados = await visualizarOrcamentoPublico(token);
      setEstado({ loading: false, erro: null, dados });
    } catch (err) {
      setEstado({
        loading: false,
        erro: { status: err.status, mensagem: err.message },
        dados: null,
      });
    }
  };

  useEffect(() => {
    if (token) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAprovar = async () => {
    setSalvando(true);
    setErroAcao(null);
    try {
      await aprovarOrcamentoPublico(token);
      setSucesso({
        titulo: "Orçamento aprovado!",
        mensagem: "Em breve você sera contatado para os próximos passos.",
        tone: "success",
      });
    } catch (err) {
      setErroAcao(err.message || "Não foi possível concluir. Tente de novo.");
    } finally {
      setSalvando(false);
      setConfirmando(null);
    }
  };

  const handleRecusar = async () => {
    if (!motivo.trim()) return;
    setSalvando(true);
    setErroAcao(null);
    try {
      await recusarOrcamentoPublico(token, motivo.trim());
      setSucesso({
        titulo: "Resposta registrada.",
        mensagem: "Obrigado pelo retorno — sua resposta foi enviada ao vendedor.",
        tone: "neutral",
      });
    } catch (err) {
      setErroAcao(err.message || "Não foi possível concluir. Tente de novo.");
    } finally {
      setSalvando(false);
      setConfirmando(null);
    }
  };

  if (estado.loading) {
    return (
      <Container>
        <Header><HeaderInner><Title>PharuERP</Title></HeaderInner></Header>
        <Card>
          <StateBox $tone="neutral">
            <FiClock size={28} aria-hidden="true" />
            <p>Carregando orçamento...</p>
          </StateBox>
        </Card>
      </Container>
    );
  }

  if (estado.erro) {
    const { status, mensagem } = estado.erro;
    let titulo = "Não foi possível carregar";
    let detalhe = mensagem;
    if (status === 404) {
      titulo = "Link não encontrado";
      detalhe = "Verifique se o link está correto ou solicite um novo ao vendedor.";
    } else if (status === 410) {
      titulo = "Link expirado";
      detalhe = "Este link não está mais disponível. Solicite um novo ao vendedor.";
    } else if (status === 429) {
      titulo = "Muitas tentativas";
      detalhe = "Por segurança, aguarde alguns segundos e tente novamente.";
    }
    return (
      <Container>
        <Header><HeaderInner><Title>PharuERP</Title></HeaderInner></Header>
        <Card>
          <StateBox $tone="danger">
            <FiAlertCircle size={32} aria-hidden="true" />
            <h2>{titulo}</h2>
            <p>{detalhe}</p>
          </StateBox>
        </Card>
      </Container>
    );
  }

  if (sucesso) {
    return (
      <Container>
        <Header><HeaderInner><Title>PharuERP</Title></HeaderInner></Header>
        <Card>
          <StateBox $tone={sucesso.tone}>
            <FiCheck size={36} aria-hidden="true" />
            <h2>{sucesso.titulo}</h2>
            <p>{sucesso.mensagem}</p>
          </StateBox>
        </Card>
      </Container>
    );
  }

  const dados = estado.dados;
  const total = dados.valor_total;

  // Botoes de decisao — mesmos blocos nos dois layouts: com template eles
  // vivem numa faixa centralizada ABAIXO da pagina full-bleed (padrao ja
  // provado no ContratoPublico); sem template, dentro do Card legado.
  const acoes = (
    <>
      {/* Copia em PDF — sempre disponivel (backend tem layout minimo
          de fallback quando a unidade nao configurou template). */}
      <ActionBar style={{ marginTop: 8 }}>
        <SecondaryButton
          as="a"
          href={urlPdf}
          download
          style={{ textDecoration: "none", textAlign: "center" }}
        >
          <FiDownload size={16} aria-hidden="true" />
          {" "}Baixar PDF
        </SecondaryButton>
      </ActionBar>

      {erroAcao && (
        <StateBox $tone="danger" role="alert" style={{ marginTop: 12 }}>
          <FiAlertCircle size={20} aria-hidden="true" />
          <p>{erroAcao}</p>
        </StateBox>
      )}

      {dados.pode_aprovar && (
        <ActionBar>
          <ApproveButton
            type="button"
            onClick={() => setConfirmando("aprovar")}
            disabled={salvando}
          >
            <FiCheck size={18} aria-hidden="true" />
            Aprovar orçamento
          </ApproveButton>
          <RejectButton
            type="button"
            onClick={() => setConfirmando("revisar")}
            disabled={salvando}
          >
            <FiEdit3 size={18} aria-hidden="true" />
            Revisar
          </RejectButton>
        </ActionBar>
      )}

      {/* Bloco F.5: pode_revisar sem pode_aprovar = link gerado em
          RASCUNHO (cliente vendo cedo) ou APROVADO (cliente quer
          ajustar antes do contrato). Mostra so o botao Revisar. */}
      {!dados.pode_aprovar && dados.pode_revisar && (
        <ActionBar>
          <RejectButton
            type="button"
            onClick={() => setConfirmando("revisar")}
            disabled={salvando}
          >
            <FiEdit3 size={18} aria-hidden="true" />
            Pedir revisão
          </RejectButton>
        </ActionBar>
      )}

      {!dados.pode_aprovar && !dados.pode_revisar && renderEstadoNaoAcionavel(dados)}
    </>
  );

  return (
    <Container>
      <Header>
        <HeaderInner>
          <Title>PharuERP</Title>
          <Subtitle>Orçamento {dados.numero}</Subtitle>
        </HeaderInner>
      </Header>

      {htmlTemplate ? (
        // Proposta no template padrao da marcenaria (publicado + padrao):
        // FULL-BLEED — o design do template ocupa a viewport inteira como
        // pagina web (o proprio template decide larguras via .container).
        // Nada de card/max-width do ERP por cima; era isso que espremia o
        // template numa "folha A4" de 760px no meio da tela.
        <>
          <HtmlTemplateFrame
            fullBleed
            html={htmlTemplate}
            titulo={`Orçamento ${dados.numero}`}
          />
          <ActionArea>{acoes}</ActionArea>
        </>
      ) : (
      <Card>
        <>
        <Section>
          <Label>Cliente</Label>
          <Value>{dados.cliente_nome}</Value>
        </Section>

        <Section style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
          <div>
            <Label>Situação</Label>
            <Value>{SITUACAO_LABEL[dados.situacao] || dados.situacao}</Value>
          </div>
          <div>
            <Label>Enviado em</Label>
            <Value>{fmtData(dados.enviado_em)}</Value>
          </div>
          <div>
            <Label>Validade</Label>
            <Value>{dados.validade_dias} dias</Value>
          </div>
          <div>
            <Label>Valor total</Label>
            <Value style={{ fontSize: "1.3rem", fontWeight: 700 }}>{fmtMoeda(total)}</Value>
          </div>
        </Section>

        <Section>
          <Label>Ambientes</Label>
          {dados.ambientes.length === 0 ? (
            <p style={{ color: "#888" }}>Nenhum ambiente cadastrado.</p>
          ) : (
            dados.ambientes.map((amb, idx) => (
              <AmbienteCard key={idx}>
                <AmbienteHeader>
                  <strong>{amb.nome}</strong>
                  <span>{fmtMoeda(amb.subtotal)}</span>
                </AmbienteHeader>
                {amb.itens.length > 0 && (
                  <ItensTable>
                    <thead>
                      <tr>
                        <th style={{ width: "55%" }}>Descrição</th>
                        <th style={{ width: "15%", textAlign: "right" }}>Qtd</th>
                        <th style={{ width: "15%", textAlign: "right" }}>Valor</th>
                        <th style={{ width: "15%", textAlign: "right" }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amb.itens.map((i, k) => (
                        <tr key={k}>
                          <td>{i.descricao}</td>
                          <td style={{ textAlign: "right" }}>
                            {Number(i.quantidade).toLocaleString("pt-BR")} {i.unidade_medida}
                          </td>
                          <td style={{ textAlign: "right" }}>{fmtMoeda(i.valor_unitario)}</td>
                          <td style={{ textAlign: "right" }}>{fmtMoeda(i.valor_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </ItensTable>
                )}
              </AmbienteCard>
            ))
          )}
        </Section>

        <TotalRow>
          <span>Total geral</span>
          <strong>{fmtMoeda(total)}</strong>
        </TotalRow>
        </>

        {acoes}
      </Card>
      )}

      {confirmando === "aprovar" && (
        <Modal onClick={() => setConfirmando(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar aprovação</h3>
            <p>Você confirma a aprovação do orçamento {dados.numero} no valor de <strong>{fmtMoeda(total)}</strong>?</p>
            <ModalActions>
              <SecondaryButton type="button" onClick={() => setConfirmando(null)} disabled={salvando}>
                Cancelar
              </SecondaryButton>
              <PrimaryButton type="button" onClick={handleAprovar} disabled={salvando}>
                {salvando ? "Aprovando..." : "Sim, aprovar"}
              </PrimaryButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}

      {/* Bloco F.5: "Recusar" → "Revisar" com comentario obrigatorio.
          Cliente pede ajuste; vendedor recebe email e ajusta o orcamento. */}
      {confirmando === "revisar" && (
        <Modal onClick={() => setConfirmando(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Solicitar revisão</h3>
            <p>
              Descreva abaixo o que você gostaria de alterar — o vendedor
              receberá sua mensagem e entrará em contato para ajustar.
            </p>
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={5}
              maxLength={1000}
              placeholder="Ex: gostaria de mais opções de cor; preciso revisar o prazo; o valor está acima do esperado..."
              autoFocus
              required
            />
            <ModalActions>
              <SecondaryButton type="button" onClick={() => setConfirmando(null)} disabled={salvando}>
                Cancelar
              </SecondaryButton>
              <RejectButton type="button" onClick={handleRecusar} disabled={salvando || !motivo.trim()}>
                {salvando ? "Enviando..." : "Enviar solicitação"}
              </RejectButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

/**
 * Bloco H — UI contextual por situacao quando cliente nao pode mais
 * aprovar/recusar. Mensagens especificas por estado evitam o frase
 * confusa generica "Este orcamento ja foi {situacao}".
 */
function renderEstadoNaoAcionavel(dados) {
  const data = (iso) => (iso ? fmtData(iso) : "");
  switch (dados.situacao) {
    case "APROVADO":
      return (
        <StateBox $tone="success">
          <FiCheck size={32} aria-hidden="true" />
          <h2>Você já aprovou este orçamento!</h2>
          <p>
            Aprovado em {data(dados.aprovado_em)}. Em breve você será contatado
            para os próximos passos da produção.
          </p>
        </StateBox>
      );
    case "CONVERTIDO":
      return (
        <StateBox $tone="success">
          <FiCheck size={32} aria-hidden="true" />
          <h2>Convertido em pedido</h2>
          <p>
            Aprovado em {data(dados.aprovado_em)} e convertido em pedido firmado.
            Você será contatado para o cronograma de produção.
          </p>
        </StateBox>
      );
    case "RECUSADO":
      return (
        <StateBox $tone="neutral">
          <FiX size={32} aria-hidden="true" />
          <h2>Você recusou este orçamento</h2>
          <p>
            Recusado em {data(dados.recusado_em)}.
            {dados.motivo_recusa ? ` Motivo: ${dados.motivo_recusa}.` : ""}
            {" "}Caso queira reabrir a negociação, entre em contato com o vendedor.
          </p>
        </StateBox>
      );
    case "EXPIRADO":
      return (
        <StateBox $tone="warning">
          <FiAlertCircle size={32} aria-hidden="true" />
          <h2>Orçamento expirado</h2>
          <p>
            Este orçamento não está mais válido. Solicite um novo ao vendedor
            para receber valores atualizados.
          </p>
        </StateBox>
      );
    case "RASCUNHO":
      // Defensivo: backend agora bloqueia geracao de link em RASCUNHO,
      // mas links antigos pre-Bloco H podem cair aqui.
      return (
        <StateBox $tone="neutral">
          <FiClock size={32} aria-hidden="true" />
          <h2>Orçamento em preparação</h2>
          <p>
            O vendedor ainda está finalizando este orçamento. Em breve você
            receberá um link atualizado.
          </p>
        </StateBox>
      );
    default:
      return (
        <StateBox $tone="neutral">
          <FiAlertCircle size={32} aria-hidden="true" />
          <h2>Ação indisponível</h2>
          <p>
            Este orçamento não pode ser respondido neste momento. Entre em
            contato com o vendedor.
          </p>
        </StateBox>
      );
  }
}

export default OrcamentoPublico;
